"use client"

import { useCallback, useEffect, useState } from "react"
import { ArrowUpRight, Copy, Check } from "lucide-react"
import { apiUrl, authHeaders } from "@/lib/api"
import { fireCouponConfetti } from "@/lib/coupon-confetti"
import FetchError from "./fetch-error"

interface Coupon {
  id: number
  codigo: string
  desconto?: string
  vantagem?: string
  discount_percentage?: number
  status?: string
  data_inicio?: string
  data_fim?: string
  created_at?: string
}

function timeAgo(date?: string): string {
  if (!date) return ""
  const min = Math.floor((Date.now() - new Date(date).getTime()) / 60000)
  if (min < 1) return "agora"
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

const statusStyle: Record<string, string> = {
  ativo: "text-emerald-600 dark:text-emerald-400",
  embreve: "text-amber-600 dark:text-amber-400",
  inativo: "text-muted-foreground",
  expirado: "text-rose-600 dark:text-rose-400",
  desconhecido: "text-muted-foreground",
}
const statusLabel: Record<string, string> = {
  ativo: "ativo",
  embreve: "em breve",
  inativo: "inativo",
  expirado: "expirado",
  desconhecido: "—",
}

export default function UserActivity() {
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [copied, setCopied] = useState<number | null>(null)
  const [totalAtivos, setTotalAtivos] = useState(0)

  const load = useCallback(() => {
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
    const headers = authHeaders(token)
    fetch(`${apiUrl()}/coupons/?ordering=-created_at&page_size=6`, { headers })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        setCoupons((data.results || (Array.isArray(data) ? data : [])).slice(0, 6))
        setError(false)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))

    fetch(`${apiUrl()}/coupons/?status=ativo&page_size=1`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setTotalAtivos(data.count || 0))
      .catch(() => {})
  }, [])

  useEffect(() => {
    load()
    // Cupom é o dado que a operadora vigia no pico — refaz a leitura no mesmo
    // ritmo da navbar pra "há X min" nunca crescer sobre dado congelado.
    const id = setInterval(load, 60_000)
    return () => clearInterval(id)
  }, [load])

  function copyCode(e: React.MouseEvent<HTMLButtonElement>, id: number, code: string) {
    const trigger = e.currentTarget
    navigator.clipboard.writeText(code).then(() => {
      setCopied(id)
      fireCouponConfetti(trigger)
      setTimeout(() => setCopied(null), 1800)
    })
  }

  return (
    <div className="surface-card flex h-full flex-col">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Códigos</div>
          <h3 className="mt-1 font-display text-2xl font-normal tracking-tight text-foreground">
            Cupons recentes
          </h3>
        </div>
        <a
          href="/dashboard-cms/cupons"
          className="group inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Ver kanban
          <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </div>

      <div className="mt-5 flex-1">
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-12 animate-pulse rounded bg-muted/30" />)}
          </div>
        ) : error && coupons.length === 0 ? (
          <FetchError what="os cupons" onRetry={load} className="min-h-[200px]" />
        ) : coupons.length === 0 ? (
          <div className="flex h-full min-h-[200px] items-center justify-center text-center">
            <p className="max-w-[260px] text-sm text-muted-foreground">
              Nenhum código capturado ainda hoje. Janela mais ativa: <span className="text-foreground">09h–11h</span> BRT.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {coupons.map((c) => {
              const status = c.status || "ativo"
              const discount = c.discount_percentage ? `-${c.discount_percentage}%` : c.desconto || ""
              const isCopied = copied === c.id
              return (
                <li key={c.id} className="group flex items-center gap-3 py-3 -mx-2 px-2 rounded-md transition-colors hover:bg-muted/20">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-3">
                      {/* O código inteiro é alvo de cópia — no celular acertar
                          só o ícone de 26px era loteria. */}
                      <button
                        type="button"
                        onClick={(e) => copyCode(e, c.id, c.codigo)}
                        title={`Copiar ${c.codigo}`}
                        className="tap-44 rounded-sm font-mono text-[15px] font-semibold tracking-tight text-foreground transition-colors hover:text-[var(--brand-amber)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-amber)]/40"
                      >
                        {c.codigo}
                      </button>
                      {discount && (
                        <span className="font-mono text-xs font-medium text-[var(--brand-amber)]">
                          {discount}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-baseline gap-2 text-xs">
                      <span className={`uppercase tracking-[0.14em] text-[10px] ${statusStyle[status] || statusStyle.ativo}`}>
                        {statusLabel[status] || status}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-mono text-muted-foreground">{timeAgo(c.created_at)}</span>
                      {c.vantagem && (
                        <>
                          <span className="text-muted-foreground">·</span>
                          <span className="truncate text-muted-foreground" title={c.vantagem}>{c.vantagem}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={(e) => copyCode(e, c.id, c.codigo)}
                    className="tap-44 flex-shrink-0 rounded-md border border-border/60 bg-background/60 p-2 text-muted-foreground transition-colors hover:border-[var(--brand-amber)]/50 hover:text-foreground"
                    title="Copiar código"
                    aria-label={`Copiar ${c.codigo}`}
                  >
                    {isCopied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-border/40 pt-4 text-xs text-muted-foreground tabular-nums">
        <span><span className="text-foreground">{totalAtivos}</span> ativos agora</span>
        <a href="/dashboard-cms/cupons" className="hover:text-foreground">Gerenciar →</a>
      </div>
    </div>
  )
}
